import type { CarListingFilterState } from "@/components/car-listing/listing-filters-types";

export type ListingsSaleTypeFilter = "all" | "auction" | "fixed";

export function parseIdListParam(raw: string | null | undefined): number[] {
  if (!raw?.trim()) return [];
  const seen = new Set<number>();
  const ids: number[] = [];
  for (const part of raw.split(",")) {
    const n = Number(part.trim());
    if (!Number.isInteger(n) || n <= 0 || seen.has(n)) continue;
    seen.add(n);
    ids.push(n);
  }
  return ids;
}

export function toggleSelectedValue(current: string[], value: string): string[] {
  return current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
}

export function buildListingsHref(opts: {
  saleType: ListingsSaleTypeFilter;
  brandIds: string[];
  modelId: string;
  bodyType: string;
  fuelType: string;
  search: string;
}): string {
  const params = new URLSearchParams();
  if (opts.saleType === "auction") params.set("type", "auction");
  if (opts.saleType === "fixed") params.set("type", "fixed");
  const brandIds = parseIdListParam(opts.brandIds.join(","));
  if (brandIds.length === 1) params.set("brandId", String(brandIds[0]));
  if (brandIds.length > 1) params.set("brandIds", brandIds.join(","));
  if (opts.modelId) params.set("modelId", opts.modelId);
  if (opts.bodyType) params.set("bodyType", opts.bodyType);
  if (opts.fuelType) params.set("fuelType", opts.fuelType);
  const q = opts.search.trim();
  if (q) params.set("search", q);
  const qs = params.toString();
  return qs ? `/listings?${qs}` : "/listings";
}

export function parseListingsSearchParams(search: string): {
  search: string;
  saleType?: "auction" | "fixed";
  brandIds: number[];
  modelId?: number;
  years?: number[];
  bodyTypes?: string[];
  fuelType?: string;
  fuelTypes?: string[];
} {
  const raw = search.startsWith("?") ? search.slice(1) : search;
  const params = new URLSearchParams(raw);
  const typeParam = params.get("type");
  const brandIds = parseIdListParam(params.get("brandIds") || params.get("brandId"));
  const modelIdParam = params.get("modelId");
  const yearParam = params.get("year");
  const bodyTypeParam = params.get("bodyType");
  const fuelTypeParam = params.get("fuelType");
  return {
    search: params.get("search") || "",
    saleType: typeParam === "auction" || typeParam === "fixed" ? typeParam : undefined,
    brandIds,
    modelId: modelIdParam ? Number(modelIdParam) : undefined,
    years: yearParam ? [Number(yearParam)] : undefined,
    bodyTypes: bodyTypeParam ? [bodyTypeParam] : undefined,
    fuelType: fuelTypeParam || undefined,
    fuelTypes: fuelTypeParam ? [fuelTypeParam] : undefined,
  };
}

/** URL → listings filter state on the first paint (no empty-then-hydrate flash). */
export function listingFilterStateFromSearch(search: string): CarListingFilterState {
  const parsed = parseListingsSearchParams(search);
  return {
    saleType: parsed.saleType,
    brandIds: parsed.brandIds.length ? parsed.brandIds : undefined,
    modelId: parsed.modelId,
    years: parsed.years,
    bodyTypes: parsed.bodyTypes,
    fuelType: parsed.fuelType,
    fuelTypes: parsed.fuelTypes,
  };
}
